import { useState, type CSSProperties, type FormEvent, type ReactElement } from 'react';

export interface PaginationProps {
  readonly currentPage: number;
  readonly totalPages: number;
  readonly onPageChange: (page: number) => void;
}

const btnStyle = (disabled: boolean): CSSProperties => ({
  fontSize: 12,
  padding: '4px 12px',
  border: '1px solid var(--border)',
  borderRadius: 4,
  background: 'var(--surface)',
  color: disabled ? 'var(--text-muted)' : 'var(--text)',
  cursor: disabled ? 'not-allowed' : 'pointer',
  opacity: disabled ? 0.5 : 1,
});

export function Pagination({ currentPage, totalPages, onPageChange }: PaginationProps): ReactElement | null {
  const [jumpInput, setJumpInput] = useState('');

  if (totalPages <= 1) return null;

  const atFirst = currentPage <= 1;
  const atLast = currentPage >= totalPages;

  const handleJumpSubmit = (e: FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    const n = Number.parseInt(jumpInput, 10);
    if (Number.isFinite(n) && n >= 1 && n <= totalPages) {
      onPageChange(n);
      setJumpInput('');
    }
  };

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '12px 0',
        fontSize: 13,
        color: 'var(--text-muted)',
        flexWrap: 'wrap',
        gap: 8,
      }}
    >
      <span>
        Page {currentPage} of {totalPages}
      </span>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        <button
          type="button"
          disabled={atFirst}
          onClick={() => onPageChange(1)}
          style={btnStyle(atFirst)}
          aria-label="First page"
        >
          First
        </button>
        <button
          type="button"
          disabled={atFirst}
          onClick={() => onPageChange(currentPage - 1)}
          style={btnStyle(atFirst)}
          aria-label="Previous page"
        >
          Prev
        </button>
        <button
          type="button"
          disabled={atLast}
          onClick={() => onPageChange(currentPage + 1)}
          style={btnStyle(atLast)}
          aria-label="Next page"
        >
          Next
        </button>
        <button
          type="button"
          disabled={atLast}
          onClick={() => onPageChange(totalPages)}
          style={btnStyle(atLast)}
          aria-label="Last page"
        >
          Last
        </button>
        <form onSubmit={handleJumpSubmit} style={{ display: 'inline-flex', gap: 4, marginLeft: 8 }}>
          <label htmlFor="jump-to-page" style={{ fontSize: 12 }}>
            Go to
          </label>
          <input
            id="jump-to-page"
            type="number"
            min={1}
            max={totalPages}
            value={jumpInput}
            onChange={(e) => setJumpInput(e.target.value)}
            style={{
              width: 56,
              padding: '2px 6px',
              border: '1px solid var(--border)',
              borderRadius: 4,
              fontSize: 12,
            }}
          />
        </form>
      </div>
    </div>
  );
}
